import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import ShiftForm from "@/modules/shifts/components/ShiftForm";
import { useShiftDictionary } from "@/modules/shifts/hooks/useShiftDictionary";
import { ApiError } from "@/services/api";
import { ReadShift, WriteShift } from "@/types/shift";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useEffect } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
export default function ShiftEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useShiftDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.Shifts,
  );
  const { can_update } = useModulePermissions();
  const query = useQuery<ReadShift>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => {
      const result = await config.service.get(id);
      return result as ReadShift;
    },
  });

  const mutationFn: MutationFunction<ReadShift, WriteShift> = async (values) =>
    config.service.patch(id, values);

  const mutation = useMutation<ReadShift, ApiError, WriteShift>({
    mutationFn,
    onSuccess: async (response) => {
      const id = response.id;

      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WriteShift>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues || {}) as DefaultValues<WriteShift>,
    disabled: mutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);

  useEffect(() => {
    if (query.data) {
      const formValues = Object.fromEntries(
        Object.entries(query.data).filter(([, value]) => value != null),
      ) as DefaultValues<WriteShift>;
      form.reset(formValues);
    }
  }, [form, query.data]);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && <ShiftForm />}
    </DictionaryRecordContextProvider>
  );
}
